 
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Directive, inject, Input, Optional, Self } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { FormFieldControl } from '../form-field-control.directive';

let nextUniqueId = 0;

/**
 * Represents the common inputs between PrimeNG components, that will be used in FormFieldControl.
 * It serves as alternative token to the actual PrimeNG component class which would cause unnecessary
 * retention of that component class and its component metadata.
 */
class PrimeNGWrapperComponent {
  inputId: string;
  styleClass: string;
  required: boolean;
  disabled: boolean;
}

/** An injcet function to get the `PrimeNGWrapperComponent` which represent the current CVA component. */
const injectPrimeNGWrapperComponent = (): PrimeNGWrapperComponent => {
  const valueAccessors = inject(NG_VALUE_ACCESSOR);
  return (valueAccessors?.[0] ?? {}) as PrimeNGWrapperComponent;
};

@Directive({
  selector: 'p-select, p-multiSelect, p-treeSelect, p-calendar, p-inputNumber',
  providers: [
    {
      provide: FormFieldControl,
      useExisting: PrimeWrapperFormFieldControlDirective,
    },
  ],
  standalone: true,
})
export class PrimeWrapperFormFieldControlDirective implements FormFieldControl {
  private _uid = `p-wrapper-component-${nextUniqueId++}`;
  private _pComponent = injectPrimeNGWrapperComponent();

  /** The inputId of this Dropdown. */
  @Input()
  set inputId(value: string) {
    this._pComponent.inputId = value || this._uid;
  }
  /** Implemented as part of FormFieldControl. */
  get id(): string {
    return this._pComponent.inputId;
  }

  /** The styleClass of this Dropdown. */
  @Input()
  set styleClass(value: string) {
    if (!value?.includes('w-full')) {
      this._pComponent.styleClass = `${value ?? ''} w-full`;
    }
  }

  /** Implemented as part of FormFieldControl. */
  @Input()
  set disabled(value: BooleanInput) {
    this._pComponent.disabled = coerceBooleanProperty(value);
  }
  get disabled(): boolean {
    return this._disabled ?? this.ngControl?.disabled ?? false;
  }
  private _disabled: boolean;

  /** Implemented as part of FormFieldControl. */
  @Input()
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this._pComponent.required = this._required;
  }
  get required(): boolean {
    return (
      this._required ??
      this.ngControl?.control?.hasValidator(Validators.required) ??
      false
    );
  }
  private _required: boolean;

  constructor(@Optional() @Self() private ngControl: NgControl) {
    // Force setter to be called in case the value was not specified.
    this.inputId = this.id;
    this.styleClass = '';
  }
}
